import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DownloadSimple, CircleNotch, MagnifyingGlass, ArrowLeft, Star } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUB_TABS = [
  { key: 'b1b2', label: 'W1: B1 & B2 Pair', short: 'B1 & B2', colLabel: 'B1, B2' },
  { key: 'b2b3', label: 'W2: B2 & B3 Pair', short: 'B2 & B3', colLabel: 'B2, B3' },
  { key: 'b3b4', label: 'W3: B3 & B4 Pair', short: 'B3 & B4', colLabel: 'B3, B4' },
  { key: 'b4b5', label: 'W4: B4 & B5 Pair', short: 'B4 & B5', colLabel: 'B4, B5' },
];

export default function WindowAdjacentPairs() {
  const navigate = useNavigate();
  const [pair, setPair] = useState('b1b2');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('times');
  const [sortDir, setSortDir] = useState('desc');
  const [only2026, setOnly2026] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    axios.get(`${API}/analysis/adjacent-pairs`, { params: { pair }, withCredentials: true })
      .then((res) => { if (alive) { setData(res.data); setLoading(false); } })
      .catch((err) => {
        if (!alive) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load adjacent pairs');
        setLoading(false);
      });
    return () => { alive = false; };
  }, [pair]);

  const rows = useMemo(() => {
    if (!data?.rows) return [];
    // Compute overdue ratio = current_gap / avg_bounce_back (matches W37 convention).
    let out = data.rows.map((r) => {
      const ratio = r.avg_bounce_back && r.avg_bounce_back > 0 && r.current_gap != null
        ? r.current_gap / r.avg_bounce_back
        : null;
      return { ...r, overdue_ratio: ratio };
    });
    const q = search.trim();
    if (q) {
      out = out.filter((r) => r.pair.includes(q) || `${r.b1}` === q || `${r.b2}` === q);
    }
    if (only2026) out = out.filter((r) => (r.count_2026 || 0) > 0);
    const dirMult = sortDir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dirMult;
      if (av > bv) return 1 * dirMult;
      return (a.b1 - b.b1) || (a.b2 - b.b2);
    });
    return out;
  }, [data, search, sortKey, sortDir, only2026]);

  // Overdue Summary — top 10 most-overdue pairs (by ratio DESC) whose hit-rate
  // is > 0.5%. We always return up to 10 regardless of whether the ratio hits
  // the traditional 2× overdue threshold — matches the user's ask to "show top
  // 10 overdue". Rows still get intensity styling based on ratio bands below.
  const overdueSummary = useMemo(() => {
    if (!data?.rows) return [];
    return data.rows
      .map((r) => {
        const ratio = r.avg_bounce_back && r.avg_bounce_back > 0 && r.current_gap != null
          ? r.current_gap / r.avg_bounce_back
          : null;
        return { ...r, overdue_ratio: ratio };
      })
      .filter((r) => r.overdue_ratio != null && r.percentage > 0.005)
      .sort((a, b) => b.overdue_ratio - a.overdue_ratio)
      .slice(0, 10);
  }, [data]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir('desc');
    }
  };

  const handleExport = () => {
    if (!data?.rows?.length) return;
    const wb = XLSX.utils.book_new();
    const sheetData = rows.map((r) => ({
      [activeTab.colLabel]: r.pair,
      'Times': r.times,
      'Percentage': r.percentage,
      'In 2026 Came (times)': r.count_2026 || 0,
      'In 2025 Came (times)': r.count_2025 || 0,
      'In 2024 Came (times)': r.count_2024 || 0,
      'Avg Bounce Back (draws)': r.avg_bounce_back,
      'Current Gap (draws)': r.current_gap,
      'Overdue (×N)': r.overdue_ratio != null ? Number(r.overdue_ratio.toFixed(2)) : '',
      'Last Occurrence Draw #': r.last_occurrence_draw,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    // Percentage formatting
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = 1; R <= range.e.r; ++R) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (cell) cell.z = '0.00%';
    }
    XLSX.utils.book_append_sheet(wb, ws, `${activeTab.short.replace(/ /g, '')} Pairs`);
    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `AdjacentPairs_${pair}_${today}.xlsx`);
    toast.success('Excel exported');
  };

  const activeTab = SUB_TABS.find((t) => t.key === pair) || SUB_TABS[0];

  const arrow = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="page-adjacent-pairs">
      {/* Standard window header — matches Window 8 etc. */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700" data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
              <Star size={24} weight="fill" className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 66</h1>
              <p className="text-xs text-gray-500">Pairs of Adjacent Balls — currently: <b>{activeTab.short} pair</b></p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Repeat-in-2026 Summary — for each of the 4 adjacent-pair slices
            (W1-W4), show every pair that has already come twice or more in
            2026. Sits at the very top of the page, above the cross-pair
            consensus. */}
        <Repeat2026Summary />

        {/* 3x-per-Year Baseline — for each of the 4 adjacent-pair slices,
            how many distinct pairs hit 3x-in-a-year over the last 10
            complete years, averaged, so a pair already at 2x in 2026 can
            be judged against the historical baseline for extending to 3x. */}
        <ThreeXPerYearSummary />

        {/* Cross-pair consensus — numbers overdue across ≥2 ball-position slices */}
        <CrossPairConsensus />

        {/* Sub-tab selector */}
        <div className="rounded-xl border border-indigo-200 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-wrap gap-1 px-4 py-2">
            {SUB_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setPair(t.key)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-black uppercase tracking-wider border ${pair === t.key ? 'text-white bg-indigo-600 border-indigo-600' : 'text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-700'}`}
                data-testid={`adj-pairs-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <CircleNotch size={22} className="animate-spin mr-2" />
          <span>Loading adjacent-pair aggregates…</span>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-rose-800 text-sm" data-testid="adj-pairs-error">
          <b>Error:</b> {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* 🔥 Overdue Summary — top-of-window "square" showing pairs with
              percentage > 0.5% AND current_gap ≥ 2× avg_bounce_back, ordered
              by most-overdue first. Uses the same W37 visual language. */}
          {overdueSummary.length > 0 && (
            <div className="rounded-2xl border-2 border-rose-500 bg-white shadow-lg overflow-hidden" data-testid="adj-pairs-overdue-summary">
              <div className="px-5 py-3 bg-gradient-to-r from-rose-700 via-rose-600 to-orange-600 text-white flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider">🔥 Top 10 Overdue Pairs ({activeTab.short})</h3>
                  <p className="text-[11px] text-white/85 italic">Top 10 pairs with hit-rate <b>&gt; 0.5%</b>, ranked by current gap ÷ avg bounce-back (most-overdue first).</p>
                </div>
                <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="adj-pairs-overdue-count">
                  {overdueSummary.length} overdue pair{overdueSummary.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="adj-pairs-overdue-table">
                  <thead className="bg-rose-50/70 border-b-2 border-rose-200 sticky top-0">
                    <tr>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-14">#</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Pair</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-16">Times</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Percentage</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Years Missing</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-20">Current Gap</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-24">Avg Bounce-Back</th>
                      <th className="px-2 py-2 text-center uppercase text-rose-800 font-black w-24">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueSummary.map((r, i) => {
                      const ratio = r.overdue_ratio;
                      const pct = Math.round((ratio - 1) * 100);
                      const rowCls = ratio >= 4
                        ? 'bg-rose-100/70'
                        : ratio >= 3
                          ? 'bg-rose-50/60'
                          : (i % 2 === 0 ? 'bg-white' : 'bg-rose-50/30');
                      const badgeCls = ratio >= 4
                        ? 'bg-rose-700 text-white ring-2 ring-amber-400'
                        : ratio >= 3
                          ? 'bg-rose-600 text-white'
                          : 'bg-rose-500 text-white';
                      return (
                        <tr key={r.pair} className={`border-b border-rose-100 ${rowCls}`} data-testid={`adj-pairs-overdue-row-${i}`}>
                          <td className="px-2 py-2 text-center font-mono text-slate-400 text-[11px]">{i + 1}</td>
                          <td className="px-2 py-2 text-center font-mono font-black text-rose-700 text-[13px]">{r.pair}</td>
                          <td className="px-2 py-2 text-center font-mono font-bold text-slate-800">{r.times}</td>
                          <td className="px-2 py-2 text-center font-mono text-slate-700">{(r.percentage * 100).toFixed(2)}%</td>
                          <td className="px-2 py-2 text-center font-mono" data-testid={`adj-pairs-overdue-years-missing-${i}`}>
                            {(r.years_missing || 0) > 0
                              ? <span className="inline-block px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 font-black text-[11px]" title={`Not seen in the last ${r.years_missing} year${r.years_missing === 1 ? '' : 's'}`}>{r.years_missing} yr{r.years_missing === 1 ? '' : 's'}</span>
                              : <span className="text-emerald-600 font-bold text-[11px]">this yr</span>}
                          </td>
                          <td className="px-2 py-2 text-center font-mono font-bold text-slate-800">{r.current_gap}</td>
                          <td className="px-2 py-2 text-center font-mono text-slate-600">{r.avg_bounce_back?.toFixed(2)}</td>
                          <td className="px-2 py-2 text-center">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-black text-[11px] ${badgeCls}`}>
                              ×{ratio.toFixed(1)}
                              <span className="text-[9px] opacity-80">(+{pct}%)</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 bg-slate-50/70 border-t border-rose-100 text-[10px] text-slate-600 italic">
                <b>Overdue ×N</b> = current gap ÷ avg bounce-back. ≥ 2.0 is overdue · ≥ 3.0 is strong signal · ≥ 4.0 (ringed) is a deep-overdue anomaly. Filter: percentage &gt; 0.5%.
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="text-[11px] font-mono text-slate-600 mr-2">
              <span className="opacity-70">Total draws:</span> <b className="text-slate-900">{data.total_draws}</b>
              <span className="opacity-70 ml-3">Unique pairs:</span> <b className="text-slate-900">{data.unique_pairs}</b>
              <span className="opacity-70 ml-3">Showing:</span> <b className="text-slate-900">{rows.length}</b>
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter (e.g. 1,4 or just 4)"
                className="pl-7 pr-2 py-1 rounded border border-slate-300 text-[12px] font-mono w-56 focus:border-indigo-500 focus:outline-none"
                data-testid="adj-pairs-search"
              />
            </div>
            <label className="flex items-center gap-1.5 text-[11px] font-mono text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={only2026}
                onChange={(e) => setOnly2026(e.target.checked)}
                data-testid="adj-pairs-only-2026"
              />
              Only pairs that hit in 2026
            </label>
            <button
              onClick={handleExport}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1 rounded bg-indigo-600 text-white font-black text-[12px] hover:bg-indigo-700"
              data-testid="adj-pairs-export-excel"
              title="Download the current view as Excel"
            >
              <DownloadSimple size={14} weight="bold" />
              Excel Export
            </button>
          </div>

          {/* Table */}
          <div className="rounded-xl border border-indigo-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-[12px] border-collapse" data-testid="adj-pairs-table">
                <thead className="sticky top-0 z-10 bg-indigo-100">
                  <tr>
                    <SortableTh onClick={() => toggleSort('pair')}>{activeTab.colLabel}{arrow('pair')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('times')} className="text-right">Times{arrow('times')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('percentage')} className="text-right">Percentage{arrow('percentage')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('count_2026')} className="text-center">In 2026 Came{arrow('count_2026')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('count_2025')} className="text-center">In 2025 Came{arrow('count_2025')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('avg_bounce_back')} className="text-right">Avg Bounce Back{arrow('avg_bounce_back')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('current_gap')} className="text-right">Current Gap{arrow('current_gap')}</SortableTh>
                    <SortableTh onClick={() => toggleSort('overdue_ratio')} className="text-center">Overdue{arrow('overdue_ratio')}</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const isEven = i % 2 === 0;
                    const ratio = r.overdue_ratio;
                    const overduePct = ratio != null ? Math.round((ratio - 1) * 100) : null;
                    const badgeCls = ratio == null
                      ? ''
                      : ratio >= 4
                        ? 'bg-rose-700 text-white ring-2 ring-amber-400'
                        : ratio >= 3
                          ? 'bg-rose-600 text-white'
                          : ratio >= 2
                            ? 'bg-rose-500 text-white'
                            : ratio >= 1
                              ? 'bg-amber-500 text-white'
                              : 'bg-emerald-100 text-emerald-800';
                    return (
                      <tr key={r.pair} className={isEven ? 'bg-white' : 'bg-indigo-50/30'} data-testid={`adj-pairs-row-${r.pair.replace(',', '_')}`}>
                        <td className="border border-indigo-100 px-3 py-1.5 font-mono font-black text-slate-900">{r.pair}</td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-right font-mono font-black text-slate-900">{r.times}</td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-right font-mono text-slate-700">{(r.percentage * 100).toFixed(2)}%</td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-center">
                          {(r.count_2026 || 0) > 0
                            ? <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-black text-[11px] font-mono">{r.count_2026}</span>
                            : <span className="text-slate-400 font-mono">0</span>}
                        </td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-center">
                          {(r.count_2025 || 0) > 0
                            ? <span className="inline-block px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-black text-[11px] font-mono">{r.count_2025}</span>
                            : <span className="text-slate-400 font-mono">0</span>}
                        </td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-right font-mono text-slate-700">
                          {r.avg_bounce_back != null ? r.avg_bounce_back : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="border border-indigo-100 px-3 py-1.5 text-right font-mono">
                          {r.current_gap == null ? <span className="text-slate-400">—</span> : (
                            <span className={r.current_gap === 0 ? 'text-emerald-700 font-black' : r.avg_bounce_back && r.current_gap > r.avg_bounce_back ? 'text-rose-700 font-black' : 'text-slate-700'}>
                              {r.current_gap}
                            </span>
                          )}
                        </td>
                        <td className="border border-indigo-100 px-2 py-1.5 text-center">
                          {ratio == null ? (
                            <span className="text-slate-400 font-mono">—</span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono font-black text-[10px] ${badgeCls}`}
                                  title={`Current gap ÷ avg bounce-back = ${ratio.toFixed(2)}× (${overduePct >= 0 ? '+' : ''}${overduePct}% past avg)`}>
                              ×{ratio.toFixed(1)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-400 italic">No pairs match the current filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] text-slate-600 leading-relaxed">
            <div><b>Times</b>: total draws where this exact ({activeTab.colLabel}) pair appeared. <b>Percentage</b>: times / total draws.</div>
            <div className="mt-1"><b>In 2026 Came</b> / <b>In 2025 Came</b>: number of draws in that year where this pair appeared. <b>0</b> = the pair has not yet occurred in that year.</div>
            <div className="mt-1"><b>Avg Bounce Back</b>: mean gap (in draws) between successive occurrences of this pair. <b>Current Gap</b>: draws since the pair last appeared — <span className="text-emerald-700 font-black">green = hit on the most recent draw</span>, <span className="text-rose-700 font-black">red = currently overdue (gap &gt; avg)</span>.</div>
            <div className="mt-1"><b>Overdue ×N</b> = current gap ÷ avg bounce-back. ≥ 2.0 = <span className="text-rose-600 font-black">overdue</span>, ≥ 3.0 = strong signal, ≥ 4.0 (ringed) = deep-overdue anomaly. Between 1 and 2 = amber (approaching). Under 1 = fresh (green).</div>
          </div>
        </>
      )}
      </main>
    </div>
  );
}

function SortableTh({ children, className = '', ...rest }) {
  return (
    <th
      {...rest}
      className={`border border-indigo-200 px-3 py-1.5 text-left font-black text-[11px] text-indigo-900 uppercase tracking-wider cursor-pointer select-none hover:bg-indigo-200 ${className}`}
    >
      {children}
    </th>
  );
}

/**
 * ThreeXPerYearSummary
 * For each of the 4 adjacent-pair slices (W1-W4), shows how many distinct
 * pairs reached 3-or-more occurrences within a single calendar year, for
 * each of the last 10 complete years, plus the 10-year average. Baseline
 * for judging whether a pair already at 2x in 2026 is likely to extend to
 * 3x by year end.
 */
function ThreeXPerYearSummary() {
  const [pairData, setPairData] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    Promise.all(
      SUB_TABS.map((t) =>
        axios.get(`${API}/analysis/adjacent-pairs`, { params: { pair: t.key }, withCredentials: true })
          .then((res) => [t.key, res.data])
          .catch(() => [t.key, null])
      )
    ).then((entries) => {
      if (!alive) return;
      const obj = {};
      entries.forEach(([k, v]) => { obj[k] = v; });
      setPairData(obj);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const years = SUB_TABS.map((t) => pairData[t.key]?.three_x_stats?.years).find((y) => y) || [];

  const cell = (tabKey, year) => {
    const stats = pairData[tabKey]?.three_x_stats;
    const row = stats?.per_year?.find((p) => p.year === year);
    return row || { count_3x_plus: 0, pairs: [] };
  };

  const avgFor = (tabKey) => pairData[tabKey]?.three_x_stats?.avg_3x_per_year ?? 0;

  if (loading) {
    return (
      <div className="rounded-xl border border-amber-200 bg-white shadow-sm p-4 flex items-center justify-center text-amber-700 text-[12px]" data-testid="three-x-per-year-loading">
        <CircleNotch size={16} className="animate-spin mr-2" /> Loading 3x-per-year baseline…
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-amber-400 bg-white shadow-sm overflow-hidden" data-testid="three-x-per-year-summary">
      <div className="px-5 py-3 bg-gradient-to-r from-amber-600 via-amber-500 to-orange-500 text-white flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider">📊 Pairs Hitting 3x in a Year — Last 10-Year Baseline</h3>
          <p className="text-[11px] text-white/90 italic">
            For each slice (W1-W4), how many distinct pairs reached <b>3 or more</b> occurrences within a single calendar year, per year, and the 10-year average.
            Use this to judge whether a pair already 2x in 2026 is likely to reach 3x too.
          </p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="three-x-per-year-table">
          <thead className="bg-amber-50 border-b-2 border-amber-200">
            <tr>
              <th className="px-3 py-2 text-left font-black text-amber-900 uppercase">Year</th>
              {SUB_TABS.map((t) => (
                <th key={t.key} className="px-3 py-2 text-center font-black text-amber-900 uppercase" data-testid={`three-x-per-year-col-${t.key}`}>{t.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {years.map((y, i) => (
              <tr key={y} className={i % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'} data-testid={`three-x-per-year-row-${y}`}>
                <td className="px-3 py-1.5 font-mono font-black text-slate-800">{y}</td>
                {SUB_TABS.map((t) => {
                  const c = cell(t.key, y);
                  const tooltip = c.pairs.length
                    ? c.pairs.map((p) => `(${p.pair} = ${p.count}x)`).join(', ')
                    : 'No pair reached 3x this year';
                  return (
                    <td key={t.key} className="px-3 py-1.5 text-center" title={tooltip} data-testid={`three-x-per-year-cell-${t.key}-${y}`}>
                      {c.count_3x_plus > 0 ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-black text-[11px] font-mono cursor-help">
                          {c.count_3x_plus}
                        </span>
                      ) : (
                        <span className="text-slate-400 font-mono">0</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-amber-100 border-t-2 border-amber-300" data-testid="three-x-per-year-avg-row">
              <td className="px-3 py-2 font-black text-amber-900 uppercase text-[11px]">10-Yr Avg</td>
              {SUB_TABS.map((t) => (
                <td key={t.key} className="px-3 py-2 text-center font-mono font-black text-amber-900" data-testid={`three-x-per-year-avg-${t.key}`}>
                  {avgFor(t.key).toFixed(1)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="px-4 py-2 bg-slate-50/70 border-t border-amber-100 text-[10px] text-slate-600 italic">
        Hover a cell to see which exact pairs hit 3x that year. e.g. W2 in 2025: (23,24 = 3x), (22,32 = 3x) → 2 pairs.
      </div>
    </div>
  );
}

/**
 * Repeat2026Summary
 * For each of the 4 adjacent-pair slices (W1: B1&B2, W2: B2&B3, W3: B3&B4,
 * W4: B4&B5), lists every pair that has already occurred TWICE OR MORE in
 * 2026 — a quick "which pairs are repeating this year" view, sorted by
 * count_2026 desc. Sits at the very top of the page.
 */
function Repeat2026Summary() {
  const [pairData, setPairData] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    Promise.all(
      SUB_TABS.map((t) =>
        axios.get(`${API}/analysis/adjacent-pairs`, { params: { pair: t.key }, withCredentials: true })
          .then((res) => [t.key, res.data])
          .catch(() => [t.key, null])
      )
    ).then((entries) => {
      if (!alive) return;
      const obj = {};
      entries.forEach(([k, v]) => { obj[k] = v; });
      setPairData(obj);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const perTab = useMemo(() => {
    const out = {};
    SUB_TABS.forEach((t) => {
      const rows = pairData[t.key]?.rows || [];
      out[t.key] = rows
        .filter((r) => (r.count_2026 || 0) >= 2)
        .sort((a, b) => (b.count_2026 - a.count_2026) || (b.times - a.times));
    });
    return out;
  }, [pairData]);

  const totalRepeats = SUB_TABS.reduce((s, t) => s + (perTab[t.key]?.length || 0), 0);

  if (loading) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-white shadow-sm p-4 flex items-center justify-center text-emerald-700 text-[12px]" data-testid="repeat-2026-loading">
        <CircleNotch size={16} className="animate-spin mr-2" /> Loading 2026 repeat pairs…
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-emerald-400 bg-white shadow-sm overflow-hidden" data-testid="repeat-2026-summary">
      <div className="px-5 py-3 bg-gradient-to-r from-emerald-700 via-emerald-600 to-teal-600 text-white flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider">🔁 Pairs Repeating in 2026 (2× or more)</h3>
          <p className="text-[11px] text-white/85 italic">For each slice (W1-W4), every pair that has already appeared <b>twice or more</b> in 2026, sorted by 2026 count desc.</p>
        </div>
        <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="repeat-2026-count">
          {totalRepeats} pair{totalRepeats === 1 ? '' : 's'} total
        </span>
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {SUB_TABS.map((t) => {
          const list = perTab[t.key] || [];
          return (
            <div key={t.key} className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`repeat-2026-col-${t.key}`}>
              <div className="px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex items-center justify-between">
                <span className="font-black text-sm uppercase tracking-wider">{t.label}</span>
                <span className="text-[10px] font-mono opacity-90">{list.length}</span>
              </div>
              {list.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11px] text-slate-500 italic">No pair has repeated 2× in 2026 yet.</div>
              ) : (
                <table className="w-full text-[11px]">
                  <thead className="bg-emerald-50 border-b border-emerald-200">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-black text-emerald-900 uppercase">{t.colLabel}</th>
                      <th className="px-2 py-1.5 text-center font-black text-emerald-900 uppercase">2026×</th>
                      <th className="px-2 py-1.5 text-right font-black text-emerald-900 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((r, i) => (
                      <tr key={r.pair} className={i % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'} data-testid={`repeat-2026-row-${t.key}-${r.pair.replace(',', '_')}`}>
                        <td className="px-2 py-1.5 font-mono font-black text-slate-900">{r.pair}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 font-black text-[11px] font-mono">{r.count_2026}</span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-600">{r.times}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * CrossPairConsensus
 * Aggregates the top-10 overdue pairs across all 4 adjacent-pair slices
 * (B1&B2, B2&B3, B3&B4, B4&B5) and surfaces every number that appears in
 * ≥ 2 overdue pairs across different ball-position slices. The higher the
 * "coverage" (distinct slices), the stronger the cross-position signal.
 */
function CrossPairConsensus() {
  const [pairData, setPairData] = React.useState({});
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let alive = true;
    Promise.all(
      SUB_TABS.map((t) =>
        axios.get(`${API}/analysis/adjacent-pairs`, { params: { pair: t.key }, withCredentials: true })
          .then((res) => [t.key, res.data])
          .catch(() => [t.key, null])
      )
    ).then((entries) => {
      if (!alive) return;
      const obj = {};
      entries.forEach(([k, v]) => { obj[k] = v; });
      setPairData(obj);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const consensus = useMemo(() => {
    // For each slice, take the top-10 overdue pairs (with pct > 0.5%).
    // For each number in every overdue pair, remember: how many slices it
    // appears in + the pairs + the ratios. Filter to numbers in ≥ 2 slices.
    const byNumber = new Map();
    SUB_TABS.forEach((t) => {
      const rows = pairData[t.key]?.rows || [];
      const overdue = rows
        .map((r) => {
          const ratio = r.avg_bounce_back && r.avg_bounce_back > 0 && r.current_gap != null
            ? r.current_gap / r.avg_bounce_back
            : null;
          return { ...r, overdue_ratio: ratio };
        })
        .filter((r) => r.overdue_ratio != null && r.percentage > 0.005)
        .sort((a, b) => b.overdue_ratio - a.overdue_ratio)
        .slice(0, 10);
      overdue.forEach((r) => {
        [r.b1, r.b2].forEach((n) => {
          if (!byNumber.has(n)) byNumber.set(n, { number: n, slices: new Set(), hits: [] });
          const entry = byNumber.get(n);
          entry.slices.add(t.key);
          entry.hits.push({ slice: t.key, slice_label: t.short, pair: r.pair, ratio: r.overdue_ratio, gap: r.current_gap });
        });
      });
    });
    return [...byNumber.values()]
      .filter((e) => e.slices.size >= 2)
      .map((e) => ({
        ...e,
        coverage: e.slices.size,
        max_ratio: Math.max(...e.hits.map((h) => h.ratio)),
      }))
      .sort((a, b) => (b.coverage - a.coverage) || (b.max_ratio - a.max_ratio) || (a.number - b.number));
  }, [pairData]);

  if (loading) {
    return (
      <div className="rounded-xl border border-purple-200 bg-white shadow-sm p-4 flex items-center justify-center text-purple-600 text-[12px]" data-testid="cross-pair-loading">
        <CircleNotch size={16} className="animate-spin mr-2" /> Loading cross-pair consensus…
      </div>
    );
  }

  return (
    <div className="rounded-xl border-2 border-purple-400 bg-white shadow-sm overflow-hidden" data-testid="cross-pair-consensus">
      <div className="px-5 py-3 bg-gradient-to-r from-purple-700 via-purple-600 to-fuchsia-600 text-white flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-black uppercase tracking-wider">🎯 Cross-Pair Consensus</h3>
          <p className="text-[11px] text-white/85 italic">Numbers appearing in overdue pairs across <b>≥ 2 ball-position slices</b> (B1&amp;B2, B2&amp;B3, B3&amp;B4, B4&amp;B5). Higher coverage = stronger cross-position signal.</p>
        </div>
        <span className="text-[10px] font-mono bg-white/20 rounded px-2 py-1" data-testid="cross-pair-count">
          {consensus.length} number{consensus.length === 1 ? '' : 's'} with ≥ 2-slice coverage
        </span>
      </div>
      {consensus.length === 0 ? (
        <div className="px-4 py-6 text-center text-slate-500 italic text-[12px]">
          No numbers currently span multiple overdue-pair slices.
        </div>
      ) : (
        <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
          <table className="w-full text-[12px]" data-testid="cross-pair-table">
            <thead className="sticky top-0 bg-purple-50 border-b-2 border-purple-200 z-10">
              <tr>
                <th className="px-2 py-2 text-center uppercase text-purple-800 font-black w-14">#</th>
                <th className="px-2 py-2 text-center uppercase text-purple-800 font-black w-20">Number</th>
                <th className="px-2 py-2 text-center uppercase text-purple-800 font-black w-24">Coverage</th>
                <th className="px-2 py-2 text-center uppercase text-purple-800 font-black w-24">Peak Overdue</th>
                <th className="px-2 py-2 text-left uppercase text-purple-800 font-black">Sources (slice · pair · ×N gap)</th>
              </tr>
            </thead>
            <tbody>
              {consensus.map((entry, i) => {
                const covCls = entry.coverage >= 4 ? 'bg-purple-700 text-white' : entry.coverage === 3 ? 'bg-purple-500 text-white' : 'bg-purple-200 text-purple-900';
                return (
                  <tr key={entry.number} className={`border-b border-purple-100 ${i % 2 === 0 ? 'bg-white' : 'bg-purple-50/30'}`} data-testid={`cross-pair-row-${entry.number}`}>
                    <td className="px-2 py-2 text-center font-mono text-slate-400 text-[11px]">{i + 1}</td>
                    <td className="px-2 py-2 text-center">
                      <span className="inline-flex items-center justify-center rounded-full bg-slate-800 text-white font-black w-8 h-8 text-xs">{entry.number}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full font-mono font-black text-[11px] ${covCls}`}>
                        {entry.coverage}/4 slices
                      </span>
                    </td>
                    <td className="px-2 py-2 text-center font-mono font-black text-rose-700">×{entry.max_ratio.toFixed(1)}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {entry.hits
                          .sort((a, b) => b.ratio - a.ratio)
                          .map((h, hi) => (
                            <span key={hi} className="inline-flex items-center gap-1 rounded bg-white border border-slate-200 px-1.5 py-0.5 text-[9px] font-mono">
                              <b className="text-purple-700">{h.slice_label}</b>
                              <span className="text-slate-500">{h.pair}</span>
                              <b className="text-rose-700">×{h.ratio.toFixed(1)}</b>
                            </span>
                          ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-4 py-2 bg-slate-50/70 border-t border-purple-100 text-[10px] text-slate-600 italic">
        Coverage 4/4 = number appears in an overdue pair for every ball-position slice — the strongest cross-position candidate. Peak Overdue is the highest ×N ratio among the sources.
      </div>
    </div>
  );
}
